import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";

/**
 * 管理聊天列表的所有滚动行为：
 * - 首次进入自动滚底
 * - 高度缩减修复（撤回/删除后防空白）
 * - 新消息自动滚底 / 未读计数
 * - 保存/恢复滚动位置（进入/返回子页）
 * - 表情面板高度动画（spacer 直接 DOM 操作）
 *
 * @param {object[]} msgs      - MobX 消息数组
 * @param {boolean}  active    - 页面是否激活
 * @param {string}   sessionId - 会话 ID
 * @param {boolean}  showEmoji - 表情面板是否展开
 * @param {object}   hasInitedRef - 主组件传入的首次进入标志 ref
 */
export default function useChatScrollState({ msgs, active, sessionId, showEmoji, hasInitedRef }) {
  const listRef        = useRef(null);
  const listInnerRef   = useRef(null); // 消息内容 wrapper（供外部使用，此 hook 不再用于 observe）
  const emojiSpacerRef = useRef(null);

  // 滚动状态 refs（供非 React 回调和其他 hooks 读写）
  const shouldScrollBottom     = useRef(true);
  const isAtBottomRef          = useRef(true);
  const isRestoringScrollRef   = useRef(false);
  const initScrollDoneRef      = useRef(false);
  const savedScrollTopRef      = useRef(-1);
  const wasAtBottomRef         = useRef(true);
  const prevMsgsLengthRef      = useRef(0);
  const isLoadingMoreRef       = useRef(false);
  const lastKnownNewestTimeRef = useRef(0);
  const emojiHeightTimerRef    = useRef(null);

  const [unreadCount, setUnreadCount] = useState(0);

  // ── 自动滚到底部 ───────────────────────────────────────────────────────
  const scrollToBottom = useCallback((smooth = false) => {
    if (isRestoringScrollRef.current) return;
    if (listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    }
  }, []);

  // ── 首次进入：一次性滚底（移除 ResizeObserver 避免图片加载时反复触发跳动）──
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    initScrollDoneRef.current = false;
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    // 4 秒后允许平滑滚动（新消息到来时用 smooth）
    const timer = setTimeout(() => { initScrollDoneRef.current = true; }, 4000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line
  }, [sessionId]);

  // ── 持久 ResizeObserver：修复撤回/删除后 scrollTop 超界留白 ─────────
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let prevScrollHeight = el.scrollHeight;
    const ro = new ResizeObserver(() => {
      if (isRestoringScrollRef.current) return;
      const newScrollHeight = el.scrollHeight;
      if (newScrollHeight < prevScrollHeight) {
        const maxTop = Math.max(0, newScrollHeight - el.clientHeight);
        if (el.scrollTop > maxTop) el.scrollTop = maxTop;
      }
      prevScrollHeight = newScrollHeight;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sessionId]);

  // ── 新消息到达：自动滚底 or 累积未读计数 ────────────────────────────
  // 同时依赖最后一条消息的 idClient，以捕获「条数不变但最后一条被替换」的场景
  const lastMsgId = msgs.length > 0 ? (msgs[msgs.length - 1]?.idClient ?? null) : null;
  useEffect(() => {
    const prevLen          = prevMsgsLengthRef.current;
    prevMsgsLengthRef.current = msgs.length;
    const currentNewest    = msgs.length > 0 ? (msgs[msgs.length - 1]?.time ?? 0) : 0;
    const prevNewest       = lastKnownNewestTimeRef.current;
    lastKnownNewestTimeRef.current = Math.max(currentNewest, prevNewest);
    if (prevNewest > 0 && currentNewest <= prevNewest) return;
    if (shouldScrollBottom.current || isAtBottomRef.current) {
      // 初始加载（initScrollDoneRef 还未完成）时用即时滚动，避免平滑动画导致顶部闪现
      scrollToBottom(initScrollDoneRef.current);
      setUnreadCount(0);
    } else if (msgs.length > prevLen) {
      const incoming = msgs.filter(m => (m.time ?? 0) > prevNewest && m.flow === 'in').length;
      if (incoming > 0) setUnreadCount(prev => prev + incoming);
    }
  // eslint-disable-next-line
  }, [msgs.length, lastMsgId, scrollToBottom]);

  // ── 每帧保存当前滚动位置（每次渲染后执行）────────────────────────────
  useLayoutEffect(() => {
    if (!active || !listRef.current) return;
    if (isRestoringScrollRef.current) return;
    const el = listRef.current;
    if (el.scrollHeight > el.clientHeight) {
      savedScrollTopRef.current = el.scrollTop;
      wasAtBottomRef.current    = isAtBottomRef.current;
    }
  });

  // ── 从子页返回时恢复滚动位置（绘制前执行，用户无感知） ────────────────
  useLayoutEffect(() => {
    if (!active || !hasInitedRef.current) return;
    const el = listRef.current;
    if (!el) return;
    const savedTop  = savedScrollTopRef.current >= 0 ? savedScrollTopRef.current : el.scrollHeight;
    const targetTop = wasAtBottomRef.current ? el.scrollHeight : savedTop;
    isRestoringScrollRef.current = true;
    shouldScrollBottom.current   = false;
    isAtBottomRef.current        = false;
    initScrollDoneRef.current    = true;
    el.scrollTop = targetTop;
    const rafId = requestAnimationFrame(() => {
      el.scrollTop = targetTop;
      const dist             = el.scrollHeight - el.scrollTop - el.clientHeight;
      isAtBottomRef.current  = dist < 60;
      shouldScrollBottom.current = dist < 60;
      isRestoringScrollRef.current = false;
    });
    return () => cancelAnimationFrame(rafId);
  }, [active, hasInitedRef]);

  // 记录上一次 showEmoji 值，区分「初始挂载(false→false)」和「关闭面板(true→false)」
  const prevShowEmojiRef = useRef(false);

  // ── 表情面板高度动画（直接操作 spacer DOM，规避 iOS WebKit 过渡失效）──
  useEffect(() => {
    const wasOpen = prevShowEmojiRef.current;
    prevShowEmojiRef.current = showEmoji;
    clearTimeout(emojiHeightTimerRef.current);
    const spacerEl = emojiSpacerRef.current;
    const listEl   = listRef.current;
    if (showEmoji) {
      document.documentElement.style.setProperty('--imc-emoji-height', '200px');
      if (spacerEl) { spacerEl.style.transition = 'none'; spacerEl.style.height = '200px'; }
      requestAnimationFrame(() => {
        if (isAtBottomRef.current || shouldScrollBottom.current) scrollToBottom(true);
      });
    } else {
      // 仅在「从打开→关闭」时向上回滚，初始挂载（wasOpen=false）时跳过，避免错误上滑
      if (wasOpen && listEl && isAtBottomRef.current) {
        listEl.scrollTo({ top: Math.max(0, listEl.scrollTop - 200), behavior: 'smooth' });
      }
      if (spacerEl) { spacerEl.style.transition = 'height 0.25s ease'; spacerEl.style.height = '0px'; }
      emojiHeightTimerRef.current = setTimeout(() => {
        document.documentElement.style.setProperty('--imc-emoji-height', '0px');
      }, 250);
    }
  }, [showEmoji, scrollToBottom]);

  // 强制重载时调用：重置"最新消息时间"记录，确保下次消息加载后能触发滚底
  const resetScrollForFreshLoad = useCallback(() => {
    lastKnownNewestTimeRef.current = 0;
    shouldScrollBottom.current     = true;
  }, []);

  return {
    listRef,
    listInnerRef,
    emojiSpacerRef,
    scrollToBottom,
    shouldScrollBottom,
    isAtBottomRef,
    isRestoringScrollRef,
    initScrollDoneRef,
    savedScrollTopRef,
    wasAtBottomRef,
    unreadCount,
    setUnreadCount,
    resetScrollForFreshLoad,
  };
}
